import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LifeBuoy, Send, Loader2, Check, Upload, X, Image } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { API } from "@/utils/constants";

export const TroubleshootView = ({ user }) => {
  const [form, setForm] = useState({ name: user?.name || "", company: "", email: user?.email || "", description: "" });
  const [screenshots, setScreenshots] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    const newScreenshots = files.map(f => ({ file: f, preview: URL.createObjectURL(f), name: f.name }));
    setScreenshots(prev => [...prev, ...newScreenshots].slice(0, 5));
  };

  const removeScreenshot = (idx) => {
    setScreenshots(prev => { URL.revokeObjectURL(prev[idx].preview); return prev.filter((_, i) => i !== idx); });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.description.trim()) { toast.error("Name, email, and description are required"); return; }
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("name", form.name);
      formData.append("company", form.company);
      formData.append("email", form.email);
      formData.append("description", form.description);
      screenshots.forEach(s => formData.append("screenshots", s.file));
      await axios.post(`${API}/support-ticket`, formData, { headers: { "Content-Type": "multipart/form-data" } });
      setSubmitted(true);
      toast.success("Support ticket submitted!");
    } catch { toast.error("Failed to submit. Please try again."); }
    setSubmitting(false);
  };

  if (submitted) {
    return (
      <div className="p-4 sm:p-6 max-w-xl mx-auto">
        <div className="bg-card border rounded-2xl p-8 text-center" data-testid="ticket-success">
          <div className="w-16 h-16 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-5">
            <Check className="h-8 w-8 text-emerald-500" />
          </div>
          <h3 className="text-xl font-bold mb-2">Ticket Submitted!</h3>
          <p className="text-sm text-muted-foreground mb-6">We've received your report and will look into it. You'll receive an email update once it's resolved.</p>
          <Button onClick={() => { setSubmitted(false); setForm({ name: user?.name || "", company: "", email: user?.email || "", description: "" }); setScreenshots([]); }} variant="outline">Submit Another</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-xl mx-auto">
      <div className="mb-6">
        <h2 className="text-xl font-bold mb-1 flex items-center gap-2" data-testid="troubleshoot-title">
          <LifeBuoy className="h-5 w-5 text-primary" />Troubleshooting & Support
        </h2>
        <p className="text-sm text-muted-foreground">Having an issue? Describe it below and we'll get back to you.</p>
      </div>

      <form onSubmit={handleSubmit} className="bg-card border rounded-xl p-6 space-y-4" data-testid="support-form">
        <div>
          <label className="text-sm font-medium mb-1.5 block">Name *</label>
          <Input value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Your name" data-testid="support-name" required />
        </div>
        <div>
          <label className="text-sm font-medium mb-1.5 block">Company</label>
          <Input value={form.company} onChange={(e) => setForm(f => ({ ...f, company: e.target.value }))} placeholder="Your company" data-testid="support-company" />
        </div>
        <div>
          <label className="text-sm font-medium mb-1.5 block">Email *</label>
          <Input type="email" value={form.email} onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))} placeholder="you@company.com" data-testid="support-email" required />
        </div>
        <div>
          <label className="text-sm font-medium mb-1.5 block">Describe the issue *</label>
          <Textarea value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))} placeholder="What happened? What did you expect? Steps to reproduce..." rows={5} data-testid="support-description" required />
        </div>
        <div>
          <label className="text-sm font-medium mb-1.5 block">Screenshots <span className="text-muted-foreground font-normal">(optional, max 5)</span></label>
          <div className="flex flex-wrap gap-3 mb-2">
            {screenshots.map((s, i) => (
              <div key={i} className="relative group w-20 h-20 rounded-lg border overflow-hidden">
                <img src={s.preview} alt={s.name} className="w-full h-full object-cover" />
                <button type="button" onClick={() => removeScreenshot(i)} className="absolute top-0.5 right-0.5 bg-black/60 rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                  <X className="h-3 w-3 text-white" />
                </button>
              </div>
            ))}
            {screenshots.length < 5 && (
              <label className="w-20 h-20 rounded-lg border-2 border-dashed border-muted-foreground/30 flex flex-col items-center justify-center cursor-pointer hover:border-primary/50 transition-colors">
                <Image className="h-5 w-5 text-muted-foreground mb-1" />
                <span className="text-[10px] text-muted-foreground">Add</span>
                <input type="file" accept="image/*" multiple onChange={handleFileChange} className="hidden" />
              </label>
            )}
          </div>
        </div>
        <Button type="submit" className="w-full" disabled={submitting} data-testid="support-submit">
          {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
          {submitting ? "Submitting..." : "Submit Ticket"}
        </Button>
      </form>
    </div>
  );
};
